import { captureException } from '@sentry/react';
import axiosInstance from 'app/axios';
import { isAxiosError } from 'axios';
import { getToken, isTokenExpired } from './authorization';
import { onMaintenanceMode, onMutateError, onNotAcceptedEndUserAgreement, preserveAuthorizationHeaders, refreshAuthToken, setupAuthorizationHeader } from './axios-interceptor-handlers';
import { endUnauthorizedSession } from './session';
export const onRequest = async config => {
  const request = setupAuthorizationHeader(config);
  const token = getToken();
  if (token && isTokenExpired(token)) {
    // Wait for the refresh to complete (either new or already in progress)
    try {
      const response = await refreshAuthToken();
      request.headers.Authorization = response.headers.authorization;
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 401) await endUnauthorizedSession();
      }
      return Promise.reject(error);
    }
  }
  return request;
};
export const onResponse = response => {
  preserveAuthorizationHeaders(response);
  return response;
};
// Opt a request out of the generic mutation error toast, for the few endpoints whose contract puts
// an expected outcome on an error status — DELETE /oauth/authorize answers a denied consent with
// 400 plus the redirect the browser has to follow — or whose screen reports the failure in its own
// words, as the sign-ins page does when a logout or disconnect fails. Spread it into the request config.
export const skipMutationErrorToast = {
  _skipMutationErrorToast: true
};
const mutateMethods = ['patch', 'post', 'put', 'delete'];

// Expected refresh failures (token expired or already used) are handled by
// redirecting the user to re-authenticate, so they should not be reported to Sentry.
const expectedRefreshErrorCodes = new Set(['refresh_token_expired', 'refresh_token_used']);
const refreshTokenAndRetry = async (instance, error, originalRequest) => {
  try {
    const response = await refreshAuthToken();
    // Retry the request with the new token.
    // This prevents backoff from react-query on subsequent request failures.
    const newToken = response.headers.authorization;
    originalRequest.headers = originalRequest.headers ?? {};
    originalRequest.headers.Authorization = newToken;
    originalRequest._hasRefreshedToken = true;
    return instance(originalRequest);
  } catch (e) {
    const refreshErrorCode = isAxiosError(e) ? e.response?.data?.errorCode : undefined;
    if (!refreshErrorCode || !expectedRefreshErrorCodes.has(refreshErrorCode)) {
      captureException(error, {
        tags: {
          error: JSON.stringify(error?.response?.data),
          errorStatus: String(error.status)
        }
      });
    }
    if (isAxiosError(e) && e.response?.status === 401) await endUnauthorizedSession();
    throw e;
  }
};
const createResponseErrorHandler = instance => async error => {
  const method = error.config?.method;
  const isMutating = method && mutateMethods.includes(method);
  const originalRequest = error.config;
  if (!originalRequest || originalRequest._hasRefreshedToken) {
    throw error;
  }
  const status = error.response?.status;
  const errorCode = error.response?.data?.errorCode;
  const authorization = getToken();
  if (status === 401 && authorization) {
    return refreshTokenAndRetry(instance, error, originalRequest);
  }
  if (status === 403 && errorCode === 'latest_end_user_agreement_not_accepted') {
    onNotAcceptedEndUserAgreement();
  }
  if (status === 503 && errorCode === 'channels_service_unavailable') {
    // Resolve instead of reject to bypass the channels service-unavailable error: the
    // POST surfaces it via the isMutating error toast below.
    return error;
  }
  if (status === 503 && errorCode === 'maintenance') {
    onMaintenanceMode();
  }
  if (isMutating && !originalRequest._skipMutationErrorToast) onMutateError(error);
  throw error;
};

// Exported for backwards compatibility (used by activestorage and tests)
export const onResponseError = createResponseErrorHandler(axiosInstance);
export const setupInterceptors = instance => {
  instance.interceptors.request.use(onRequest, undefined);
  instance.interceptors.response.use(onResponse, createResponseErrorHandler(instance));
};